import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, X, CheckCircle, Clock } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const CALLBACK_REASONS = [
  "Première consultation",
  "Suivi régulier",
  "Renouvellement d'ordonnance",
  "Téléconsultation",
  "Urgence",
  "Bilan annuel",
  "Visite de contrôle",
  "Autre (logistique)",
];

const CALLBACK_TIMES = ["Matin (8h-12h)", "Après-midi (12h-18h)", "Peu importe"];

export const CallbackRequestModal = ({ doctor, open, onClose, defaultPhone }) => {
  const [phone, setPhone] = useState("");
  const [reason, setReason] = useState("");
  const [preferredTime, setPreferredTime] = useState("Peu importe");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setPhone(defaultPhone || "");
      setReason("");
      setPreferredTime("Peu importe");
      setSuccess(false);
      setError("");
    }
  }, [open, defaultPhone]);

  if (!open || !doctor) return null;

  const submit = async () => {
    if ((phone || "").replace(/\D/g, "").length < 8) {
      setError("Veuillez saisir un numéro de téléphone valide");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const token = localStorage.getItem("doctodays_token");
      await axios.post(`${API_URL}/callbacks`, {
        doctor_id: doctor.id,
        phone,
        reason: reason || null,
        preferred_time: preferredTime,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'envoi de la demande");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="callback-request-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] overflow-y-auto"
      >
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Phone className="w-4 h-4 text-emerald-600" /> Demander un rappel
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="callback-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {success ? (
          <div className="p-8 text-center" data-testid="callback-success">
            <CheckCircle className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
            <p className="font-semibold text-slate-900 mb-1">Demande envoyée !</p>
            <p className="text-slate-600 text-sm mb-6">
              Dr. {doctor.last_name} vous rappellera au {phone}. Vous pouvez suivre votre demande depuis votre espace patient.
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-colors"
              data-testid="callback-success-close"
            >
              Fermer
            </button>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <p className="text-sm text-slate-600">
              Dr. {doctor.first_name} {doctor.last_name} vous rappellera dès que possible au numéro indiqué.
            </p>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Votre numéro de téléphone</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="06 12 34 56 78"
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="callback-phone-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Motif (optionnel)</label>
              <div className="flex flex-wrap gap-2">
                {CALLBACK_REASONS.map((r) => (
                  <button
                    key={r}
                    onClick={() => setReason(reason === r ? "" : r)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      reason === r
                        ? "bg-emerald-600 text-white border-emerald-600"
                        : "bg-slate-50 text-slate-600 border-slate-200 hover:border-emerald-300"
                    }`}
                    data-testid={`callback-reason-${r}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Quand préférez-vous être rappelé ?</label>
              <div className="flex flex-wrap gap-2">
                {CALLBACK_TIMES.map((t) => (
                  <button
                    key={t}
                    onClick={() => setPreferredTime(t)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      preferredTime === t
                        ? "bg-emerald-600 text-white border-emerald-600"
                        : "bg-slate-50 text-slate-600 border-slate-200 hover:border-emerald-300"
                    }`}
                    data-testid={`callback-time-${t}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <p className="text-red-600 text-sm bg-red-50 border border-red-100 rounded-lg p-3" data-testid="callback-error">
                {error}
              </p>
            )}

            <button
              onClick={submit}
              disabled={submitting}
              className="w-full bg-emerald-600 text-white py-3 rounded-xl font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50"
              data-testid="callback-submit-btn"
            >
              {submitting ? "Envoi..." : "Envoyer ma demande de rappel"}
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
};

const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  handled: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};

const STATUS_LABELS = {
  pending: "En attente de rappel",
  handled: "Rappel effectué",
  cancelled: "Annulée",
};

export const MyCallbackRequests = () => {
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    try {
      const token = localStorage.getItem("doctodays_token");
      const res = await axios.get(`${API_URL}/callbacks/mine`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data || []);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const cancel = async (id) => {
    if (!window.confirm("Annuler cette demande de rappel ?")) return;
    try {
      const token = localStorage.getItem("doctodays_token");
      await axios.patch(`${API_URL}/callbacks/${id}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      load();
    } catch {
      alert("Erreur lors de l'annulation");
    }
  };

  const visible = items.filter((i) => i.status !== "cancelled").slice(0, 5);
  if (visible.length === 0) return null;

  return (
    <div className="mb-8" data-testid="my-callbacks-section">
      <h2 className="text-lg font-semibold text-slate-900 mb-4">Mes demandes de rappel</h2>
      <div className="space-y-3">
        {visible.map((cb) => (
          <div key={cb.id} className="bg-white rounded-xl p-4 border border-slate-100 flex items-center gap-4 flex-wrap" data-testid={`callback-item-${cb.id}`}>
            <div className="w-10 h-10 bg-sky-50 rounded-xl flex items-center justify-center flex-shrink-0">
              <Phone className="w-5 h-5 text-sky-600" />
            </div>
            <div className="flex-1 min-w-[180px]">
              <p className="font-semibold text-slate-900 text-sm">{cb.doctor_name}</p>
              <p className="text-xs text-slate-500">
                {[cb.reason, cb.preferred_time].filter(Boolean).join(" · ")}
              </p>
            </div>
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${STATUS_STYLES[cb.status] || ""}`}>
              <Clock className="w-3 h-3" /> {STATUS_LABELS[cb.status] || cb.status}
            </span>
            {cb.status === "pending" && (
              <button
                onClick={() => cancel(cb.id)}
                className="px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg text-sm transition-colors"
                data-testid={`callback-cancel-${cb.id}`}
              >
                Annuler
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
